"use client";

import { useEffect, useRef, useState } from "react";
import { useLanguage } from "@/context/language-context";
import { cn } from "@/lib/utils";

export default function ManifestoFlow({ reverse = false }: { reverse?: boolean }) {
  const { content } = useLanguage();
  const containerRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  const manifestoItems = content?.manifesto?.items;

  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;

    const observer = new IntersectionObserver(([entry]) => setIsVisible(entry.isIntersecting), {
      rootMargin: "120px",
    });

    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const Separator = () => (
    <div className="aspect-square h-3 w-3 rounded-full bg-foreground/10 sm:h-4 sm:w-4 md:h-5 md:w-5 xl:h-6 xl:w-6" />
  );

  const trackClassName = cn(
    "flex min-w-full shrink-0 animate-scroll items-center justify-around gap-8 pr-8 xl:gap-16 xl:pr-16",
    reverse && "direction-reverse",
    !isVisible && "motion-safe:[animation-play-state:paused]",
  );

  return (
    <div
      ref={containerRef}
      className="pointer-events-none relative w-full overflow-hidden border-y border-border/50 bg-background py-10 select-none"
    >
      <div className="pointer-events-none absolute top-0 bottom-0 left-0 z-10 w-24 bg-linear-to-r from-background to-transparent md:w-40" />

      <div className="pointer-events-none absolute top-0 right-0 bottom-0 z-10 w-24 bg-linear-to-l from-background to-transparent md:w-40" />

      <div className="marquee-track pointer-events-auto flex w-full overflow-hidden">
        <div className={trackClassName}>
          {manifestoItems.map((item: string, index: number) => (
            <div key={`t1-${index}`} className="flex items-center gap-8 xl:gap-16">
              <span className="text-4xl font-bold whitespace-nowrap text-foreground/25 uppercase sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl">
                {item}
              </span>
              <Separator />
            </div>
          ))}
        </div>

        <div className={trackClassName}>
          {manifestoItems.map((item: string, index: number) => (
            <div key={`t2-${index}`} className="flex items-center gap-8 xl:gap-16">
              <span className="text-4xl font-bold whitespace-nowrap text-foreground/25 uppercase sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl">
                {item}
              </span>
              <Separator />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
